/**
 * The arithmetic behind a month grid, kept away from the rendering.
 *
 * All of it works in plain year/month/day numbers and `YYYY-MM-DD` keys rather
 * than in `Date` instances, and that is the point. An event's `date` is a
 * calendar day the API sends as UTC midnight, so reading it back with
 * `getDate()` in any negative-offset zone — every American one — returns the day
 * before, and the event lands in the wrong cell. Comparing the first ten
 * characters of the two strings cannot make that mistake.
 *
 * Phase times are different: those are real instants, and are formatted in the
 * viewer's own zone where they are shown.
 */
import { calendarDay, type CalendarDay } from '@lib/validators/temporal.validator'

/** One cell of the grid. `key` is the day it stands for, `inMonth` whether it belongs. */
export interface MonthDay {
  key: string
  day: number
  inMonth: boolean
}

/** A month, as the two numbers everything here takes. */
export interface MonthRef {
  year: number
  /** 0–11, matching `Date.getMonth()`. */
  month: number
}

const WEEKS_SHOWN = 6
const DAYS_IN_WEEK = 7

function pad(n: number): string {
  return n < 10 ? `0${String(n)}` : String(n)
}

/** `YYYY-MM-DD` for a year/month/day, with no zone anywhere near it. */
export function keyFor(year: number, month: number, day: number): string {
  return `${String(year)}-${pad(month + 1)}-${pad(day)}`
}

/** The day part of an API date. Takes the string as given — see the note above. */
export function dayKeyOf(iso: string): string {
  return iso.slice(0, 10)
}

/** The viewer's own today, as a day key, for marking the grid. */
export function todayKey(now: Date = new Date()): string {
  return keyFor(now.getFullYear(), now.getMonth(), now.getDate())
}

export function daysInMonth(year: number, month: number): number {
  return new Date(Date.UTC(year, month + 1, 0)).getUTCDate()
}

/**
 * Six weeks of cells starting on `weekStartsOn`, always the same length.
 *
 * Fixed at six rows because a five-row month and a six-row month next to each
 * other make the whole page jump when you page through — and the grid is the
 * thing being read.
 */
export function monthGrid({ year, month }: MonthRef, weekStartsOn = 0): MonthDay[] {
  const firstWeekday = new Date(Date.UTC(year, month, 1)).getUTCDay()
  const lead = (firstWeekday - weekStartsOn + DAYS_IN_WEEK) % DAYS_IN_WEEK
  const cells: MonthDay[] = []
  const prev = addMonths({ year, month }, -1)
  const prevDays = daysInMonth(prev.year, prev.month)
  for (let i = lead; i > 0; i--) {
    const day = prevDays - i + 1
    cells.push({ key: keyFor(prev.year, prev.month, day), day, inMonth: false })
  }
  const total = daysInMonth(year, month)
  for (let day = 1; day <= total; day++) {
    cells.push({ key: keyFor(year, month, day), day, inMonth: true })
  }
  const next = addMonths({ year, month }, 1)
  let day = 1
  while (cells.length < WEEKS_SHOWN * DAYS_IN_WEEK) {
    cells.push({ key: keyFor(next.year, next.month, day), day, inMonth: false })
    day += 1
  }
  return cells
}

export function addMonths({ year, month }: MonthRef, delta: number): MonthRef {
  const absolute = year * 12 + month + delta
  return { year: Math.floor(absolute / 12), month: ((absolute % 12) + 12) % 12 }
}

/**
 * The window to ask the API for, covering the whole grid rather than the month.
 *
 * The leading and trailing cells belong to the neighboring months and still show
 * chips; asking only for the month itself would leave them convincingly empty.
 */
export function gridRange(
  month: MonthRef,
  weekStartsOn = 0,
): { from: CalendarDay; to: CalendarDay } {
  const cells = monthGrid(month, weekStartsOn)
  // Inclusive days, both ends. This used to append `T00:00:00.000Z` and
  // `T23:59:59.999Z`, which is the end-of-day trick that is wrong against a
  // `date` column — see `venueCalendarQuerySchema`.
  const first = cells[0]?.key ?? ''
  const last = cells[cells.length - 1]?.key ?? ''
  return { from: calendarDay(first), to: calendarDay(last) }
}

const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
]

export function monthLabel({ year, month }: MonthRef): string {
  return `${MONTH_NAMES[month] ?? ''} ${String(year)}`
}

/** The month a day key belongs to, for opening the calendar on a given event. */
export function monthOf(dayKey: string): MonthRef {
  const [year = '1970', month = '01'] = dayKey.split('-')
  return { year: Number(year), month: Number(month) - 1 }
}

/** The month the viewer is in now. */
export function currentMonth(now: Date = new Date()): MonthRef {
  return { year: now.getFullYear(), month: now.getMonth() }
}
